import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.hydrabyte.co/erm';

// Create axios instance
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 60000, // 60 seconds for OCR
});

// Request interceptor for error handling
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response) {
            // Server responded with error status
            const message = error.response.data?.message || error.message;
            throw new Error(Array.isArray(message) ? message.join(', ') : message);
        } else if (error.request) {
            // Request made but no response
            throw new Error('Không thể kết nối đến server. Vui lòng kiểm tra kết nối mạng.');
        } else {
            // Something else happened
            throw new Error(error.message);
        }
    }
);

/**
 * API 1: Generate QR Code
 * @param {string} formUrl - URL of the registration form
 * @param {string} orgId - Organization ID
 * @returns {Promise<Object>} QR code data
 */
export const generateQR = async (formUrl, orgId) => {
    const response = await apiClient.post('/utils/priority-customer/generate-qr', {
        formUrl,
        orgId,
    });
    return response.data;
};

/**
 * API 2: OCR Customer Information
 * @param {string[]} images - Array of base64 images (CCCD, VNeID, or employee card)
 * @returns {Promise<Object>} Extracted customer information with documents array
 */
export const ocrCustomerInfo = async (images) => {
    const response = await apiClient.post('/utils/priority-customer/ocr-customer-info', {
        images,
    });
    return response.data;
};

/**
 * API 3: Request Email OTP (optional)
 * Gửi OTP đến email KH. Không bắt buộc — nếu KH không có email thì bỏ qua.
 * Từ v0.4.0, API /create không còn verify OTP nữa.
 * @param {string} email - Customer email
 * @param {string} orgId - Organization ID
 * @returns {Promise<Object>} OTP request result
 */
export const requestEmailOTP = async (email, orgId) => {
    const response = await apiClient.post('/utils/priority-customer/request-email-otp', {
        email,
        orgId,
    });
    return response.data;
};

/**
 * API 4: Create Priority Customer
 * @param {Object} payload - Customer registration data
 * @param {string} payload.fullName - Full name (required)
 * @param {string} payload.phoneNumber - Phone number (required, used for duplicate check)
 * @param {string} payload.address - Address (required)
 * @param {string} payload.employeeCode - Employee code (required, min 4 chars)
 * @param {string} [payload.email] - Email (optional, triggers welcome email if provided)
 * @param {string} [payload.idNumber] - ID number / CCCD (optional)
 * @param {string} [payload.dateOfBirth] - Date of birth DD/MM/YYYY (optional)
 * @param {string} [payload.gender] - Gender Nam/Nữ (optional)
 * @param {string} payload.qrToken - QR token from URL
 * @param {Object} [payload.images] - Images object (optional)
 * @param {string} [payload.images.cccd] - CCCD image base64 (optional)
 * @param {string} [payload.images.vneid] - VNeID image base64 (optional)
 * @param {string} [payload.images.employeeCard] - Employee card image base64 (optional)
 * @returns {Promise<Object>} Created customer data
 */
export const createCustomer = async (payload) => {
    const response = await apiClient.post('/utils/priority-customer/create', payload);
    return response.data;
};

/**
 * Helper: Extract orgId from JWT token
 * @param {string} token - JWT token
 * @returns {string|null} Organization ID or null if invalid
 */
export const extractOrgIdFromToken = (token) => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.orgId || payload.metadata?.orgId || null;
    } catch (error) {
        console.error('Failed to extract orgId from token:', error);
        return null;
    }
};

const api = {
    generateQR,
    ocrCustomerInfo,
    requestEmailOTP,
    createCustomer,
    extractOrgIdFromToken,
};

export default api;
